/* eslint-disable new-cap, @typescript-eslint/no-empty-function */
/**
 * Delivery API hooks for managing delivery emails
 * Calls Python API endpoints at strix-api /api/deliveries
 */
import { fetchAuthSession } from "@aws-amplify/auth";
import { useCallback, useEffect, useState } from "react";

const DISTRIBUTION_API_URL =
  process.env.REACT_APP_API_ENDPOINT?.replace(/\/graphql$/, "") || "";

// =====================================
// Types
// =====================================

export interface UserSummary {
  id: string;
  name: string;
  email: string;
}

/** 配信メール一覧アイテム */
export interface DeliveryItem {
  id: string;
  status: string;
  fromEmail: string;
  fromName: string | null;
  subject: string;
  groupName: string | null;
  totalRecipients: number;
  successCount: number;
  failureCount: number;
  includeSender: boolean;
  scheduledAt: string | null;
  sentAt: string | null;
  createdBy: UserSummary;
  createdAt: string;
}

/** 配信メール詳細 */
export interface DeliveryDetail extends DeliveryItem {
  bodyText: string;
  bodyHtml: string | null;
  groupId: string | null;
  updatedAt: string;
}

/** 配信メール一覧レスポンス */
export interface DeliveryListResponse {
  items: DeliveryItem[];
  total: number;
  page: number;
  limit: number;
}

/** 配信メール作成リクエスト */
export interface CreateDeliveryInput {
  groupId: string;
  fromEmail: string;
  fromName?: string;
  subject: string;
  bodyText: string;
  bodyHtml?: string;
  includeSender?: boolean;
  scheduledAt?: string;
}

/** 配信メール更新リクエスト */
export interface UpdateDeliveryInput {
  groupId?: string;
  fromEmail?: string;
  fromName?: string;
  subject?: string;
  bodyText?: string;
  bodyHtml?: string;
  includeSender?: boolean;
  scheduledAt?: string | null;
}

/** 予約配信リクエスト */
export interface ScheduleDeliveryInput {
  scheduledAt: string;
}

/** 配信実行レスポンス */
export interface SendDeliveryResponse {
  id: string;
  status: string;
  totalRecipients: number;
  message: string;
}

/** 宛先プレビューアイテム */
export interface RecipientPreviewItem {
  email: string;
  name: string | null;
  companyName: string | null;
  isActive: boolean;
}

/** 宛先プレビューレスポンス */
export interface RecipientPreviewResponse {
  recipients: RecipientPreviewItem[];
  total: number;
}

/** 配信先レスポンス */
export interface DeliveryRecipient {
  id: string;
  email: string;
  name: string | null;
  companyName: string | null;
  status: string;
  sentAt: string | null;
  errorMessage: string | null;
}

/** 配信先一覧レスポンス */
export interface RecipientListResponse {
  items: DeliveryRecipient[];
  total: number;
  page: number;
  limit: number;
}

// =====================================
// Fetch helper
// =====================================

/**
 * 認証トークンを取得する
 * @return {Promise<string | null>} トークン文字列またはnull
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (_e) {
    return null;
  }
}

/**
 * 配信APIへのリクエストを実行する
 * @param {string} method HTTPメソッド
 * @param {string} path APIパス
 * @param {unknown} body リクエストボディ
 * @return {Promise<T>} レスポンスデータ
 */
async function fetchDeliveryApi<T>(
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const token = await getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const options: RequestInit = { method, headers };

  if (body) {
    options.body = JSON.stringify(body);
  }

  const res = await fetch(`${DISTRIBUTION_API_URL}${path}`, options);

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(
      (errorBody as { detail?: string }).detail ||
        `API error: ${res.status} ${res.statusText}`,
    );
  }

  if (res.status === 202 || res.status === 204) {
    if (res.status === 204) return {} as T;
  }

  return res.json();
}

// =====================================
// Delivery API Hooks
// =====================================

export interface UseDeliveriesApiOptions {
  status?: "draft" | "scheduled" | "history";
  page?: number;
  limit?: number;
}

export interface UseDeliveriesApiResult {
  data: DeliveryListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<DeliveryListResponse>;
}

/**
 * 配信メール一覧取得
 * @param {UseDeliveriesApiOptions} options オプション
 * @return {UseDeliveriesApiResult} 配信一覧データとローディング状態
 */
export function useDeliveriesApi(
  options: UseDeliveriesApiOptions = {},
): UseDeliveriesApiResult {
  const [data, setData] = useState<DeliveryListResponse | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchDeliveries =
    useCallback(async (): Promise<DeliveryListResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const params = new URLSearchParams();
        if (options.status) params.append("status", options.status);
        if (options.page) params.append("page", String(options.page));
        if (options.limit) params.append("limit", String(options.limit));

        const queryString = params.toString();
        const path = `/api/deliveries${queryString ? `?${queryString}` : ""}`;

        const responseData = await fetchDeliveryApi<DeliveryListResponse>(
          "GET",
          path,
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }, [options.status, options.page, options.limit]);

  useEffect(() => {
    fetchDeliveries().catch(() => {});
  }, [fetchDeliveries]);

  return { data, error, loading, refetch: fetchDeliveries };
}

export interface UseDeliveryApiResult {
  data: DeliveryDetail | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<DeliveryDetail>;
}

/**
 * 配信メール詳細取得
 * @param {string} deliveryId 配信メールID
 * @return {UseDeliveryApiResult} 配信詳細データとローディング状態
 */
export function useDeliveryApi(deliveryId: string): UseDeliveryApiResult {
  const [data, setData] = useState<DeliveryDetail | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchDelivery = useCallback(async (): Promise<DeliveryDetail> => {
    setLoading(true);
    setError(undefined);

    try {
      const responseData = await fetchDeliveryApi<DeliveryDetail>(
        "GET",
        `/api/deliveries/${deliveryId}`,
      );
      setData(responseData);
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [deliveryId]);

  useEffect(() => {
    if (deliveryId) {
      fetchDelivery().catch(() => {});
    }
  }, [deliveryId, fetchDelivery]);

  return { data, error, loading, refetch: fetchDelivery };
}

/**
 * 配信メール作成
 * @return {object} 作成関数とローディング状態
 */
export function useCreateDeliveryApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const createDelivery = useCallback(
    async (input: CreateDeliveryInput): Promise<DeliveryDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchDeliveryApi<DeliveryDetail>(
          "POST",
          "/api/deliveries",
          input,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createDelivery, loading, error };
}

/**
 * 配信メール更新
 * @return {object} 更新関数とローディング状態
 */
export function useUpdateDeliveryApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateDelivery = useCallback(
    async (id: string, input: UpdateDeliveryInput): Promise<DeliveryDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchDeliveryApi<DeliveryDetail>(
          "PUT",
          `/api/deliveries/${id}`,
          input,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateDelivery, loading, error };
}

/**
 * 配信メール削除
 * @return {object} 削除関数とローディング状態
 */
export function useDeleteDeliveryApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const deleteDelivery = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(undefined);

    try {
      await fetchDeliveryApi<void>("DELETE", `/api/deliveries/${id}`);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteDelivery, loading, error };
}

/**
 * 即時配信実行
 * @return {object} 送信関数とローディング状態
 */
export function useSendDeliveryApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const sendDelivery = useCallback(
    async (id: string): Promise<SendDeliveryResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchDeliveryApi<SendDeliveryResponse>(
          "POST",
          `/api/deliveries/${id}/send`,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { sendDelivery, loading, error };
}

/**
 * 予約配信設定
 * @return {object} 予約関数とローディング状態
 */
export function useScheduleDeliveryApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const scheduleDelivery = useCallback(
    async (
      id: string,
      input: ScheduleDeliveryInput,
    ): Promise<DeliveryDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchDeliveryApi<DeliveryDetail>(
          "POST",
          `/api/deliveries/${id}/schedule`,
          input,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { scheduleDelivery, loading, error };
}

/**
 * 予約キャンセル
 * @return {object} キャンセル関数とローディング状態
 */
export function useCancelDeliveryApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const cancelDelivery = useCallback(
    async (id: string): Promise<DeliveryDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchDeliveryApi<DeliveryDetail>(
          "POST",
          `/api/deliveries/${id}/cancel`,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { cancelDelivery, loading, error };
}

/**
 * 宛先プレビュー
 * @return {object} プレビュー関数とローディング状態
 */
export function usePreviewRecipientsApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const previewRecipients = useCallback(
    async (groupId: string): Promise<RecipientPreviewResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchDeliveryApi<RecipientPreviewResponse>(
          "POST",
          "/api/deliveries/preview-recipients",
          { groupId },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { previewRecipients, loading, error };
}

export interface UseDeliveryRecipientsApiOptions {
  deliveryId: string;
  page?: number;
  limit?: number;
}

/**
 * 宛先一覧（配信結果）
 * @param {UseDeliveryRecipientsApiOptions} options オプション
 * @return {object} 宛先一覧データとローディング状態
 */
export function useDeliveryRecipientsApi(
  options: UseDeliveryRecipientsApiOptions,
): {
  data: RecipientListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<RecipientListResponse>;
} {
  const [data, setData] = useState<RecipientListResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchRecipients =
    useCallback(async (): Promise<RecipientListResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const params = new URLSearchParams();
        if (options.page) params.append("page", String(options.page));
        if (options.limit) params.append("limit", String(options.limit));

        const queryString = params.toString();
        const path = `/api/deliveries/${options.deliveryId}/recipients${queryString ? `?${queryString}` : ""}`;

        const responseData = await fetchDeliveryApi<RecipientListResponse>(
          "GET",
          path,
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }, [options.deliveryId, options.page, options.limit]);

  useEffect(() => {
    if (options.deliveryId) {
      fetchRecipients().catch(() => {});
    }
  }, [options.deliveryId, fetchRecipients]);

  return { data, error, loading, refetch: fetchRecipients };
}
